import type { ColorCrosswalkField, ColorCrosswalkModifier } from "./colorCrosswalk.js";
import type { ColorCrosswalkCard } from "./colorCrosswalkPropose.js";

const FIELD_LABEL: Record<string, string> = {
  series: "series",
  roof_style: "roof style",
  package: "package",
  roof_type: "roof type",
  roof_color: "roof color",
  siding_type: "siding type",
  siding_color: "siding color",
  trim_color: "trim color",
  addon_name: "add-on",
};

export type ColorCrosswalkDraft = {
  seriesOn: boolean;
  extraSeries: string[];
  optional: string[];
  finishCode: string;
  sourceField?: ColorCrosswalkField | "";
  sourceValue?: string;
  pendingMemberId?: string;
};

export function modifierKey(modifier: ColorCrosswalkModifier): string {
  return `${modifier.field}:${modifier.op}:${modifier.value}`;
}

function oxford(parts: string[], conjunction: "and" | "or"): string {
  if (parts.length <= 1) return parts[0] ?? "";
  if (parts.length === 2) return `${parts[0]} ${conjunction} ${parts[1]}`;
  return `${parts.slice(0, -1).join(", ")}, ${conjunction} ${parts[parts.length - 1]}`;
}

function situationClause(modifier: ColorCrosswalkModifier): string {
  const name = FIELD_LABEL[modifier.field] ?? modifier.field;
  if (modifier.op === "contains") return `add-on contains ${modifier.value}`;
  if (modifier.op === "one_of") {
    const values = modifier.value.split(",").map((part) => part.trim()).filter(Boolean);
    return `${name} is ${oxford(values, "or")}`;
  }
  return `${name} is ${modifier.value}`;
}

/** The order color Save crosswalk will store. Falls back to the card suggestion. */
export function selectedCrosswalkSource(
  card: ColorCrosswalkCard,
  draft: ColorCrosswalkDraft,
): { field: ColorCrosswalkField; value: string } {
  return {
    field: draft.sourceField || card.source_field,
    value: draft.sourceValue || card.source_value,
  };
}

/** The tags Save crosswalk will store for this card. */
export function selectedCrosswalkModifiers(card: ColorCrosswalkCard, draft: ColorCrosswalkDraft): ColorCrosswalkModifier[] {
  const seriesValues = [
    ...(draft.seriesOn ? card.modifiers.filter((modifier) => modifier.field === "series").map((modifier) => modifier.value) : []),
    ...draft.extraSeries,
  ];
  const modifiers: ColorCrosswalkModifier[] = [];
  if (seriesValues.length === 1) modifiers.push({ field: "series", op: "equals", value: seriesValues[0]! });
  if (seriesValues.length > 1) modifiers.push({ field: "series", op: "one_of", value: seriesValues.join(", ") });
  for (const key of draft.optional) {
    const modifier = card.optional_modifiers.find((item) => modifierKey(item) === key);
    if (modifier) modifiers.push(modifier);
  }
  return modifiers;
}

/** Plain-language reading of the row that would be saved. */
export function colorCrosswalkConclusion(card: ColorCrosswalkCard, draft: ColorCrosswalkDraft): string {
  const source = selectedCrosswalkSource(card, draft);
  const conditions = [
    `${FIELD_LABEL[source.field] ?? source.field} is ${source.value}`,
    ...selectedCrosswalkModifiers(card, draft).map(situationClause),
  ];
  const when = `When ${oxford(conditions, "and")}`;
  const family = card.use_family.replace(/_/g, " ");
  const code = draft.finishCode.trim();
  if (!code) return `${when}, choose the finish code for each ${family} length.`;
  return `${when}, each ${family} length uses its own ${code} variant.`;
}
